<template>
    <nav aria-label="stronicowanie">
        <ul v-if="pagination.page_count > 1" class="c-pagination">
            <template v-if="currentPage > 1">
                <li class="c-pagination__page">
                    <a
                        :href="
                            currentPage === 2
                                ? `${pathname}`
                                : createPageLink(currentPage - 1)
                        "
                        aria-label="Poprzednia strona"
                        @click.prevent="setPage(currentPage - 1)"
                    >
                        <Chevron class="icon icon--rotate-180" />
                    </a>
                </li>
            </template>

            <li
                v-for="(page, key) in pages"
                :key="`left-${page}-${key}`"
                class="c-pagination__page"
            >
                <span v-if="page === '...'" class="c-pagination__dots"
                    >...</span
                >
                <a
                    v-else
                    :class="{ 'is-active': page === currentPage }"
                    :href="
                        page === 1 ? `${pathname}` : createPageLink(page, key)
                    "
                    :aria-current="page === currentPage ? 'page' : null"
                    :aria-label="
                        typeof page === 'number' ? `strona ${page}` : null
                    "
                    @click.prevent="setPage(page, key)"
                    v-text="page"
                />
            </li>

            <template v-if="currentPage < pagination.page_count">
                <li class="c-pagination__page">
                    <a
                        :href="createPageLink(currentPage + 1)"
                        aria-label="Następna strona"
                        @click.prevent="setPage(currentPage + 1)"
                    >
                        <Chevron class="icon" />
                    </a>
                </li>
            </template>
        </ul>
    </nav>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Chevron from '@icons/chevron-right.svg'
export default {
    name: 'PaginationList',
    components: {
        Chevron
    },
    computed: {
        ...mapState({
            currentPage: ({ params }) => Number(params.p),
            pagination: ({ pagination }) => pagination,
            pathname: ({ pathname }) => pathname
        }),
        pages() {
            let pages = []
            const center = [
                    this.currentPage - 2,
                    this.currentPage - 1,
                    this.currentPage,
                    this.currentPage + 1,
                    this.currentPage + 2
                ],
                filteredCenter = center.filter(
                    (p) => p > 1 && p < this.pagination.page_count
                ),
                includeThreeLeft = this.currentPage === 5,
                includeFirstPage = this.currentPage >= 4,
                includeLeftDots = this.currentPage > 5,
                includeRightDots =
                    this.currentPage < this.pagination.page_count - 3

            if (includeThreeLeft) {
                filteredCenter.unshift(2)
            }
            if (includeRightDots) {
                filteredCenter.push('...')
            }

            pages = []
            if (includeFirstPage) {
                pages.push(1)
            }
            if (includeLeftDots) {
                pages.push('...')
            }

            pages = [...pages, ...filteredCenter, this.pagination.page_count]

            if (this.currentPage < 4) {
                pages.unshift(1)
            }

            return pages
        }
    },
    mounted() {
        this.createPage()
    },
    methods: {
        ...mapActions(['setParams', 'setPageParams', 'getProducts']),
        ...mapGetters(['getURLParams']),
        createPage(page, key) {
            const params = this.getURLParams()
            params.set('p', this.determinePage(page, key))
            return `?${params.toString()}`
        },
        createPageLink(page, key) {
            return `${this.pathname}?p=${this.determinePage(page, key)}`
        },
        async setPage(page, key) {
            this.setPageParams({
                p: this.determinePage(page, key)
            })
            await this.getProducts()
            window.scrollTo(0, 0)
            this.changePageTitle(page)
            this.changeHeaderCanonical(page)
            this.changeHeaderLinks(page)

            if (document.querySelector('.category-faq') != null) {
                if (parseInt(page) !== 1) {
                    document.querySelector('.category-faq').style.display =
                        'none'
                } else {
                    document.querySelector('.category-faq').style.display =
                        'block'
                }
            }
        },
        determinePage(page, key) {
            return page === '...'
                ? key === 1
                    ? this.currentPage - 3
                    : this.currentPage + 3
                : page
        },
        changePageTitle(page) {
            const currentPageTitle = document.title
            const separator = '|'
            const pageTitleRegex = /- strona \d+/

            if (currentPageTitle.includes(separator)) {
                let updatedTitle = currentPageTitle

                if (page > 1) {
                    if (currentPageTitle.includes('strona')) {
                        const pageTitle = `- strona ${page}`
                        updatedTitle = document.title.replace(
                            pageTitleRegex,
                            pageTitle
                        )
                    } else {
                        const pageTitleParts = currentPageTitle
                            .split(separator)
                            .map((part) => part.trim())
                        updatedTitle = `${pageTitleParts[0]} - strona ${page} ${separator} ${pageTitleParts[1]}`
                    }
                } else {
                    updatedTitle = document.title.replace(pageTitleRegex, '')
                }
                document.title = updatedTitle
            }
        },
        changeHeaderCanonical(page) {
            const canonicalLink = document.querySelector(
                'link[rel="canonical"]'
            )

            if (canonicalLink == null) {
                return
            }

            const href = canonicalLink.getAttribute('href')
            const pageParamRegex = /\?p=\d+/

            if (page > 1) {
                let updatedHref
                if (href.includes('?p')) {
                    const pageTitle = `?p=${page}`
                    updatedHref = href.replace(pageParamRegex, pageTitle)
                } else {
                    updatedHref = `${href}?p=${page}`
                }
                canonicalLink.setAttribute('href', updatedHref)
            } else {
                const updatedHref = href.replace(pageParamRegex, '')
                canonicalLink.setAttribute('href', updatedHref)
            }
        },
        changeHeaderLinks(page) {
            const prevLink = document.querySelector('link[rel="prev"]'),
                nextLink = document.querySelector('link[rel="next"]'),
                lastPage = this.pagination.page_count

            if (prevLink) {
                const prevHref = prevLink.getAttribute('href')
                const prevPage = `p=${page - 1}`

                if (page === 1) {
                    prevLink.remove()
                } else if (page === 2) {
                    const updatedPrevHref = prevHref.replace(/\?p=\d+/, '')
                    prevLink.setAttribute('href', updatedPrevHref)
                } else {
                    if (prevHref.includes('?p')) {
                        const updatedPrevHref = prevHref.replace(
                            /p=\d+/,
                            prevPage
                        )
                        prevLink.setAttribute('href', updatedPrevHref)
                    } else {
                        const updatedPrevHref = `${prevHref}?p=${page - 1}`
                        prevLink.setAttribute('href', updatedPrevHref)
                    }
                }
            }

            if (nextLink) {
                const nextHref = nextLink.getAttribute('href')
                const nextPage = `p=${page + 1}`
                const updatedNextHref = nextHref.replace(/p=\d+/, nextPage)
                nextLink.setAttribute('href', updatedNextHref)

                if (page === lastPage) {
                    nextLink.remove()
                }
            }

            const canonical = document.querySelector('link[rel="canonical"]')

            if (canonical) {
                const pageHref = canonical.getAttribute('href')
                const pageRegex = /\?p=\d+/

                if (prevLink === null && page !== 1) {
                    const newPrevLink = document.createElement('link')
                    newPrevLink.rel = 'prev'

                    if (page === 2) {
                        newPrevLink.href = pageHref.replace(pageRegex, '')
                    } else {
                        newPrevLink.href = pageHref.replace(
                            pageRegex,
                            `?p=${page - 1}`
                        )
                    }

                    if (nextLink && page !== lastPage) {
                        nextLink.parentNode.insertBefore(newPrevLink, nextLink)
                    } else {
                        canonical.parentNode.insertBefore(
                            newPrevLink,
                            canonical.nextSibling
                        )
                    }
                }

                if (nextLink === null && page !== lastPage) {
                    const newNextLink = document.createElement('link')
                    newNextLink.rel = 'next'

                    if (pageHref.includes('?p')) {
                        newNextLink.href = pageHref.replace(
                            pageRegex,
                            `?p=${page + 1}`
                        )
                    } else {
                        newNextLink.href = `${pageHref}?p=${page + 1}`
                    }

                    if (prevLink && page !== 1) {
                        prevLink.parentNode.insertBefore(
                            newNextLink,
                            prevLink.nextSibling
                        )
                    } else {
                        canonical.parentNode.insertBefore(
                            newNextLink,
                            canonical.nextSibling
                        )
                    }
                }
            }
        }
    }
}
</script>
<style lang="scss">
.c-pagination {
    display: flex;
    justify-content: center;
    margin-top: -30px;

    @media #{$media-to-sm} {
        margin-top: 0;
    }

    @media #{$media-xs} {
        margin-bottom: 1rem;
    }

    a {
        color: currentColor;
    }

    &__dots {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        cursor: default;
        pointer-events: none;
    }

    &__page {
        width: 4rem;
        height: 3.2rem;
        margin: 0 0.1rem;

        a {
            display: flex;
            width: 100%;
            height: 100%;
            align-items: center;
            justify-content: center;
            border-radius: 5px;

            &:hover,
            &:focus:not(:active) {
                @include n-theme() {
                    border: 1px solid theme(color-primary);
                }
            }

            &:hover,
            &.is-active {
                @include n-theme() {
                    background: theme(color-primary);
                    color: #fff;
                }
            }
        }
    }
}
</style>
